/* Wireframe module — props are still churning each iteration, so PropTypes are
   deferred until the goserver API stabilises. Array-index keys are fine here as
   the stub lists never reorder. */
/* The modal overlay closes on click (mouse convenience) and on Escape (keyboard,
   wired in useEffect) — the two jsx-a11y interaction rules are disabled for that
   pattern only. */
/* eslint-disable react/prop-types, react/no-array-index-key, jsx-a11y/click-events-have-key-events, jsx-a11y/no-noninteractive-element-interactions */
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import emojiData from '@emoji-mart/data';
import EmojiPicker from '@emoji-mart/react';
import {
  AtSign,
  ChevronDown,
  ChevronRight,
  ChevronsUpDown,
  ChevronUp,
  Circle,
  Clock,
  Download,
  ExternalLink,
  Inbox,
  List,
  Mail,
  Maximize2,
  Minimize2,
  PanelLeftClose,
  PanelLeftOpen,
  Paperclip,
  Phone,
  Plus,
  Search,
  Send,
  Smile,
  User,
  X
} from 'lucide-react';
import { AGENT, AGENTS, CONVERSATIONS, FOLDERS, TAG_POOL, TEAM_INBOXES, VIEWS } from './stub_data';
import './support_inbox.css';

// Wireframe of the in-house email support inbox that will replace Zendesk.
// Everything here is driven by stub_data.js — no API calls yet. The goserver
// backend will later feed folders, conversations, and contact details; swap the
// stub imports for real fetches when that lands.

const FOLDER_ICONS = {
  user: User,
  at: AtSign,
  circle: Circle,
  list: List
};

// Fields a Create View filter condition can match on. Mirrors the columns the
// goserver conversation query will support for saved views.
const FILTER_FIELD_DEFS = [
  { name: 'Status', desc: 'Lifecycle state of the conversation' },
  { name: 'Priority', desc: 'Urgency level set on the ticket' },
  { name: 'Assign team', desc: 'Team inbox the conversation belongs to' },
  { name: 'Assign agent', desc: 'Agent the conversation is assigned to' },
  { name: 'Email', desc: 'Contact email address on the conversation' },
  { name: 'Tags', desc: 'Tags applied to the conversation' },
  { name: 'Created at', desc: 'When the conversation was started' },
  { name: 'Waiting since', desc: 'How long the contact has been waiting' },
  { name: 'Snoozed until', desc: 'When a snoozed conversation reopens' },
  { name: 'Last message at', desc: 'Time of the most recent message' },
  { name: 'Last interaction at', desc: 'Last activity by agent or contact' },
  { name: 'Resolved at', desc: 'When the conversation was resolved' },
  { name: 'First reply at', desc: 'Time of the first agent reply' }
];

// Operators offered per condition. Date/time fields also allow range comparisons.
const BASE_OPERATORS = ['equals', 'not equals', 'set', 'not set'];
const DATE_OPERATORS = [...BASE_OPERATORS, 'greater than', 'less than', 'between'];
const DATE_FIELDS = new Set([
  'Created at',
  'Waiting since',
  'Snoozed until',
  'Last message at',
  'Last interaction at',
  'Resolved at',
  'First reply at'
]);
const operatorsForField = (field) => (DATE_FIELDS.has(field) ? DATE_OPERATORS : BASE_OPERATORS);

// Conversation lifecycle statuses, shown in the thread-header status dropdown.
// `key` maps to the --status-*-bg/-text token pair in src/theme/tokens.css.
const STATUS_OPTIONS = [
  { label: 'Open', key: 'open' },
  { label: 'In Progress', key: 'progress' },
  { label: 'Waiting', key: 'waiting' },
  { label: 'Snoozed', key: 'snoozed' },
  { label: 'Resolved', key: 'resolved' },
  { label: 'Closed', key: 'closed' }
];

// Ticket priorities. `key` maps to the --priority-* token; 'none' clears it.
const PRIORITY_OPTIONS = [
  { key: 'urgent', label: 'Urgent' },
  { key: 'high', label: 'High' },
  { key: 'medium', label: 'Medium' },
  { key: 'low', label: 'Low' },
  { key: 'none', label: 'No priority' }
];
const priorityLabel = (key) => (PRIORITY_OPTIONS.find((p) => p.key === key) || {}).label || '';

// Team options for the Actions team picker, derived from the team inboxes.
const TEAM_OPTIONS = TEAM_INBOXES.map((t) => ({ key: t.key, label: t.label, emoji: t.emoji }));

// Statuses offered in the split Send button's "Send and set as" dropup.
const SEND_STATUS_OPTIONS = STATUS_OPTIONS.filter((s) => s.key !== 'snoozed');

// Value input for a filter condition, keyed by field: dropdown fields list their
// options; date fields get a datetime input; anything else is a free string.
const valueConfigForField = (field) => {
  if (DATE_FIELDS.has(field)) return { type: 'datetime' };
  switch (field) {
    case 'Status':
      return { type: 'options', options: STATUS_OPTIONS.map((s) => s.label) };
    case 'Priority':
      return { type: 'options', options: PRIORITY_OPTIONS.map((p) => p.label) };
    case 'Assign team':
      return { type: 'options', options: TEAM_OPTIONS.map((t) => t.label) };
    case 'Assign agent':
      return { type: 'options', options: AGENTS.map((a) => a.label) };
    case 'Tags':
      return { type: 'options', options: TAG_POOL };
    default:
      return { type: 'string' };
  }
};
// `set` / `not set` are existence checks — no value input needed.
const operatorNeedsValue = (op) => !!op && op !== 'set' && op !== 'not set';

// Sort orders for the conversation list header.
const SORT_OPTIONS = [
  'Oldest activity',
  'Newest activity',
  'Started first',
  'Started last',
  'Waiting longest',
  'Next SLA target',
  'Priority first'
];

let filterRowSeq = 0;
const newCondition = () => {
  filterRowSeq += 1;
  return { id: `cond_${filterRowSeq}`, field: '', operator: '', value: '', value2: '' };
};
const newGroup = () => {
  filterRowSeq += 1;
  return { id: `grp_${filterRowSeq}`, conditions: [newCondition()] };
};

function NavRail({ activeFolder, onSelectFolder, onCreateView, onSearch }) {
  const [teamOpen, setTeamOpen] = useState(true);
  const [viewsOpen, setViewsOpen] = useState(true);
  return (
    <nav className="si-col si-nav">
      <div className="si-nav-brand">
        <span className="si-nav-brand-title">
          <Inbox size={20} /> Inbox
        </span>
        <button type="button" className="si-nav-search" aria-label="Search" onClick={onSearch}>
          <Search size={18} />
        </button>
      </div>

      {FOLDERS.map((f) => {
        const IconEl = FOLDER_ICONS[f.icon] || List;
        return (
          <button
            key={f.key}
            type="button"
            className={`si-nav-item ${activeFolder === f.key ? 'si-active' : ''}`}
            onClick={() => onSelectFolder(f.key)}
          >
            <IconEl size={16} />
            <span className="si-nav-label">{f.label}</span>
            {f.count > 0 && <span className="si-nav-count">{f.count}</span>}
          </button>
        );
      })}

      <div className="si-section-title si-section-title-row">
        <span>Team Inboxes</span>
        <button
          type="button"
          className="si-section-add"
          aria-label={teamOpen ? 'Collapse Team Inboxes' : 'Expand Team Inboxes'}
          onClick={() => setTeamOpen((v) => !v)}
        >
          {teamOpen ? <ChevronDown size={15} /> : <ChevronRight size={15} />}
        </button>
      </div>
      {teamOpen && (
        <div className="si-nav-group">
          {TEAM_INBOXES.map((t) => (
            <button
              key={t.key}
              type="button"
              className={`si-nav-item ${activeFolder === t.key ? 'si-active' : ''}`}
              onClick={() => onSelectFolder(t.key)}
            >
              <span className="si-nav-emoji">{t.emoji}</span>
              <span className="si-nav-label">{t.label}</span>
              {t.count > 0 && <span className="si-nav-count">{t.count}</span>}
            </button>
          ))}
        </div>
      )}

      <div className="si-section-title si-section-title-row">
        <span className="si-section-left">
          Views
          <button
            type="button"
            className="si-section-add"
            aria-label="Create view"
            onClick={onCreateView}
          >
            <Plus size={15} />
          </button>
        </span>
        <button
          type="button"
          className="si-section-add"
          aria-label={viewsOpen ? 'Collapse Views' : 'Expand Views'}
          onClick={() => setViewsOpen((v) => !v)}
        >
          {viewsOpen ? <ChevronDown size={15} /> : <ChevronRight size={15} />}
        </button>
      </div>
      {viewsOpen && (
        <div className="si-nav-group">
          {VIEWS.map((v) => (
            <button
              key={v.key}
              type="button"
              className={`si-nav-item ${activeFolder === v.key ? 'si-active' : ''}`}
              onClick={() => onSelectFolder(v.key)}
            >
              <span className="si-nav-label">{v.label}</span>
            </button>
          ))}
        </div>
      )}
    </nav>
  );
}

// Small controlled dropdown: closes on outside-click or Escape while open.
function useDropdown() {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  useEffect(() => {
    if (!open) return undefined;
    const onDocClick = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    const onKey = (e) => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('mousedown', onDocClick);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDocClick);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);
  return { open, setOpen, ref };
}

function StatusFilterDropdown({ conversations }) {
  const { open, setOpen, ref } = useDropdown();
  const [status, setStatus] = useState(STATUS_OPTIONS[0]);
  const count = conversations.filter((c) => c.status === status.key).length;
  return (
    <div className="si-sort" ref={ref}>
      <button type="button" className="si-filter-btn" onClick={() => setOpen((o) => !o)}>
        {count} {status.label} <ChevronDown size={14} />
      </button>
      {open && (
        <div className="si-status-menu si-sort-menu" role="listbox">
          {STATUS_OPTIONS.map((s) => (
            <button
              key={s.key}
              type="button"
              className={`si-status-item ${s.key === status.key ? 'si-active' : ''}`}
              onClick={() => {
                setStatus(s);
                setOpen(false);
              }}
            >
              {s.label}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

function SortDropdown() {
  const { open, setOpen, ref } = useDropdown();
  const [sort, setSort] = useState('Newest activity');
  return (
    <div className="si-sort" ref={ref}>
      <button type="button" className="si-filter-btn" onClick={() => setOpen((o) => !o)}>
        {sort} <ChevronDown size={14} />
      </button>
      {open && (
        <div className="si-status-menu si-sort-menu si-sort-menu-end" role="listbox">
          {SORT_OPTIONS.map((o) => (
            <button
              key={o}
              type="button"
              className={`si-status-item ${o === sort ? 'si-active' : ''}`}
              onClick={() => {
                setSort(o);
                setOpen(false);
              }}
            >
              {o}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

function ConversationList({
  conversations,
  convState,
  selectedId,
  onSelect,
  navCollapsed,
  onToggleNav,
  title
}) {
  return (
    <div className="si-col si-list">
      <div className="si-list-head">
        <button
          type="button"
          className="si-collapse-btn"
          aria-label={navCollapsed ? 'Expand inbox bar' : 'Collapse inbox bar'}
          onClick={onToggleNav}
        >
          {navCollapsed ? <PanelLeftOpen size={18} /> : <PanelLeftClose size={18} />}
        </button>
        {title}
      </div>
      <div className="si-list-filters">
        <StatusFilterDropdown conversations={conversations} />
        <SortDropdown />
      </div>

      {conversations.map((c) => {
        const st = convState[c.id];
        const prio = st.priority;
        return (
          <div
            key={c.id}
            className={`si-conv ${selectedId === c.id ? 'si-active' : ''}`}
            role="button"
            tabIndex={0}
            onClick={() => onSelect(c.id)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                onSelect(c.id);
              }
            }}
          >
            <img className="si-avatar" src={c.contact.avatar} alt={c.contact.name} />
            <div className="si-conv-body">
              <div className="si-conv-top">
                {prio && prio !== 'none' && (
                  <span
                    className={`si-prio-dot si-prio-${prio}`}
                    title={`${priorityLabel(prio)} priority`}
                  />
                )}
                <span className="si-conv-name">{c.contact.name}</span>
                <span className="si-conv-channel">{c.channel}</span>
                <span className="si-conv-time">{c.lastActivity}</span>
              </div>
              <div className="si-conv-preview">{c.preview}</div>
              <div className="si-conv-meta">
                {st.tags.map((tag) => (
                  <span
                    key={tag}
                    className={`si-tag ${tag === 'FRD' || tag === 'RD' ? '' : 'si-tag-neutral'}`}
                  >
                    {tag}
                  </span>
                ))}
                {c.unread > 0 && <span className="si-pill">{c.unread > 9 ? '9+' : c.unread}</span>}
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
}

function Composer() {
  const [tab, setTab] = useState('reply');
  const [text, setText] = useState('');
  const [showBcc, setShowBcc] = useState(false);
  const [files, setFiles] = useState([]);
  const fileRef = useRef(null);
  const emoji = useDropdown();

  const onFilePick = (e) => {
    const picked = Array.from(e.target.files || []).map((f) => f.name);
    if (picked.length) setFiles((prev) => [...prev, ...picked]);
    e.target.value = '';
  };
  const removeFile = (idx) => setFiles((prev) => prev.filter((_, i) => i !== idx));
  const insertEmoji = (native) => {
    setText((t) => t + native);
    emoji.setOpen(false);
  };

  const [expanded, setExpanded] = useState(false);
  useEffect(() => {
    if (!expanded) return undefined;
    const onKey = (e) => {
      if (e.key === 'Escape') setExpanded(false);
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [expanded]);

  const body = (
    <>
      <div className="si-composer-tabs-row">
        <div className="si-composer-tabs" role="tablist">
          <button
            type="button"
            role="tab"
            aria-selected={tab === 'reply'}
            className={`si-tab ${tab === 'reply' ? 'si-active' : ''}`}
            onClick={() => setTab('reply')}
          >
            Reply
          </button>
          <button
            type="button"
            role="tab"
            aria-selected={tab === 'note'}
            className={`si-tab ${tab === 'note' ? 'si-active' : ''}`}
            onClick={() => setTab('note')}
          >
            Private note
          </button>
        </div>
        <button
          type="button"
          className="si-tool-btn si-composer-expand"
          aria-label={expanded ? 'Minimize composer' : 'Expand composer'}
          onClick={() => setExpanded((e) => !e)}
        >
          {expanded ? <Minimize2 size={16} /> : <Maximize2 size={16} />}
        </button>
      </div>

      {tab === 'reply' && (
        <>
          <div className="si-field">
            <span className="si-field-label">To:</span>
            <input defaultValue="yash.kadam@myyogateacher.com" />
          </div>
          <div className="si-field">
            <span className="si-field-label">Cc:</span>
            <input placeholder="Email addresses separated by comma" />
            <button type="button" className="si-bcc-btn" onClick={() => setShowBcc((v) => !v)}>
              {showBcc ? 'Remove BCC' : 'BCC'}
            </button>
          </div>
          {showBcc && (
            <div className="si-field">
              <span className="si-field-label">Bcc:</span>
              <input placeholder="Email addresses separated by comma" />
            </div>
          )}
        </>
      )}

      <textarea
        className="si-textarea"
        placeholder={
          tab === 'reply' ? 'Write a reply…' : 'Add an internal note (not sent to customer)…'
        }
        value={text}
        onChange={(e) => setText(e.target.value)}
      />

      {files.length > 0 && (
        <div className="si-attachments">
          {files.map((f, i) => (
            <span key={`${f}-${i}`} className="si-tag-chip">
              <Paperclip size={12} /> {f}
              <button type="button" aria-label={`Remove ${f}`} onClick={() => removeFile(i)}>
                <X size={13} />
              </button>
            </span>
          ))}
        </div>
      )}

      <div className="si-composer-foot">
        <button
          type="button"
          className="si-tool-btn"
          aria-label="Attach file"
          onClick={() => fileRef.current && fileRef.current.click()}
        >
          <Paperclip size={16} />
        </button>
        <input ref={fileRef} type="file" multiple hidden onChange={onFilePick} />

        <div className="si-emoji" ref={emoji.ref}>
          <button
            type="button"
            className="si-tool-btn"
            aria-label="Insert emoji"
            onClick={() => emoji.setOpen((o) => !o)}
          >
            <Smile size={16} />
          </button>
          {emoji.open && (
            <div className="si-emoji-pop">
              <EmojiPicker
                data={emojiData}
                onEmojiSelect={(e) => insertEmoji(e.native)}
                theme={document.documentElement.classList.contains('dark') ? 'dark' : 'light'}
                previewPosition="none"
                skinTonePosition="none"
              />
            </div>
          )}
        </div>

        <SendSplit label={tab === 'reply' ? 'Send' : 'Add note'} />
      </div>
    </>
  );

  if (expanded) {
    return (
      <div className="si-composer-modal-overlay">
        <div className="si-composer si-composer-modal">{body}</div>
      </div>
    );
  }
  return <div className="si-composer">{body}</div>;
}

function StatusDropdown({ initial }) {
  const [status, setStatus] = useState(initial);
  const { open, setOpen, ref } = useDropdown();

  return (
    <div className="si-status" ref={ref}>
      <button
        type="button"
        className={`si-status-trigger si-status-${status.key}`}
        onClick={() => setOpen((o) => !o)}
      >
        {status.label} <ChevronDown size={14} />
      </button>
      {open && (
        <div className="si-status-menu" role="listbox">
          {STATUS_OPTIONS.map((s) => (
            <button
              key={s.key}
              type="button"
              className={`si-status-item ${s.key === status.key ? 'si-active' : ''}`}
              onClick={() => {
                setStatus(s);
                setOpen(false);
              }}
            >
              <span className={`si-status-dot si-status-${s.key}`} />
              {s.label}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

// Split Send button: a primary action + a caret that opens a "set status" dropup.
function SendSplit({ label }) {
  const { open, setOpen, ref } = useDropdown();
  return (
    <div className="si-send-split" ref={ref}>
      <button type="button" className="si-btn si-btn-primary si-send-main">
        <Send size={14} /> {label}
      </button>
      <button
        type="button"
        className="si-btn si-btn-primary si-send-caret"
        aria-label="Send and set status"
        onClick={() => setOpen((o) => !o)}
      >
        <ChevronDown size={14} />
      </button>
      {open && (
        <div className="si-send-menu">
          <div className="si-send-menu-title">Send and set as</div>
          {SEND_STATUS_OPTIONS.map((s) => (
            <button
              key={s.key}
              type="button"
              className="si-send-menu-item"
              onClick={() => setOpen(false)}
            >
              {s.label}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

function MoreMenu() {
  const { open, setOpen, ref } = useDropdown();
  return (
    <div className="si-status" ref={ref}>
      <button
        type="button"
        className="si-btn"
        aria-label="More actions"
        onClick={() => setOpen((o) => !o)}
      >
        •••
      </button>
      {open && (
        <div className="si-status-menu" role="menu">
          <button type="button" className="si-status-item" onClick={() => setOpen(false)}>
            <Download size={15} /> Download Transcript
          </button>
        </div>
      )}
    </div>
  );
}

function PriorityBadge({ prio }) {
  if (!prio || prio === 'none') return null;
  return (
    <span className={`si-prio-badge si-prio-${prio}`}>
      <span className={`si-prio-dot si-prio-${prio}`} />
      {priorityLabel(prio)}
    </span>
  );
}

// Renderers shared by the Actions pickers and their triggers.
const renderAgent = (a) => (
  <span className="si-opt">
    <span>
      {a.label}
      {a.you && <span className="si-opt-muted"> (You)</span>}
    </span>
  </span>
);
const renderTeam = (t) => (
  <span className="si-opt">
    <span className="si-opt-emoji">{t.emoji}</span>
    {t.label}
  </span>
);
const renderPriority = (p) => (
  <span className="si-opt">
    {p.key === 'none' ? (
      <span className="si-prio-dot si-prio-none" />
    ) : (
      <span className={`si-prio-dot si-prio-${p.key}`} />
    )}
    {p.label}
  </span>
);

// Combobox: a trigger showing the current value + a searchable option list.
function SearchSelect({ value, options, placeholder, onChange, renderItem }) {
  const { open, setOpen, ref } = useDropdown();
  const [query, setQuery] = useState('');
  const q = query.trim().toLowerCase();
  const filtered = options.filter((o) => o.label.toLowerCase().includes(q));
  const choose = (o) => {
    onChange(o);
    setOpen(false);
    setQuery('');
  };
  return (
    <div className="si-select" ref={ref}>
      <button type="button" className="si-select-trigger" onClick={() => setOpen((v) => !v)}>
        <span className="si-select-value">
          {value ? renderItem(value) : <span className="si-select-ph">{placeholder}</span>}
        </span>
        <ChevronsUpDown size={15} className="si-select-caret" />
      </button>
      {open && (
        <div className="si-select-menu">
          <div className="si-select-search">
            <Search size={14} />
            <input
              // eslint-disable-next-line jsx-a11y/no-autofocus
              autoFocus
              value={query}
              placeholder="Search…"
              onChange={(e) => setQuery(e.target.value)}
            />
          </div>
          <div className="si-select-options">
            {filtered.map((o) => (
              <button
                key={o.key}
                type="button"
                className={`si-select-option ${value && o.key === value.key ? 'si-active' : ''}`}
                onClick={() => choose(o)}
              >
                {renderItem(o)}
              </button>
            ))}
            {filtered.length === 0 && <div className="si-select-empty">No matches</div>}
          </div>
        </div>
      )}
    </div>
  );
}

// Tag picker: type to filter existing tags, or create a new one on Enter / click.
function TagsInput({ tags, onChange }) {
  const { open, setOpen, ref } = useDropdown();
  const [query, setQuery] = useState('');
  const q = query.trim();
  const suggestions = TAG_POOL.filter(
    (t) => t.toLowerCase().includes(q.toLowerCase()) && !tags.includes(t)
  );
  const exists =
    tags.some((t) => t.toLowerCase() === q.toLowerCase()) ||
    TAG_POOL.some((t) => t.toLowerCase() === q.toLowerCase());
  const add = (t) => {
    const v = t.trim();
    if (v && !tags.includes(v)) onChange([...tags, v]);
    setQuery('');
  };
  const remove = (t) => onChange(tags.filter((x) => x !== t));
  return (
    <div className="si-tags-field" ref={ref}>
      <div className="si-tags-box">
        {tags.map((t) => (
          <span key={t} className="si-tag-chip">
            {t}
            <button type="button" aria-label={`Remove ${t}`} onClick={() => remove(t)}>
              <X size={13} />
            </button>
          </span>
        ))}
        <input
          className="si-tags-input"
          value={query}
          placeholder={tags.length ? '' : 'Select tags'}
          onFocus={() => setOpen(true)}
          onChange={(e) => {
            setQuery(e.target.value);
            setOpen(true);
          }}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              e.preventDefault();
              add(query);
            }
            if (e.key === 'Backspace' && !query && tags.length) {
              remove(tags[tags.length - 1]);
            }
          }}
        />
      </div>
      {open && (q.length > 0 || suggestions.length > 0) && (
        <div className="si-select-menu si-tags-menu">
          <div className="si-select-options">
            {suggestions.map((t) => (
              <button key={t} type="button" className="si-select-option" onClick={() => add(t)}>
                <span className="si-tag-chip si-tag-chip-plain">{t}</span>
              </button>
            ))}
            {q.length > 0 && !exists && (
              <button type="button" className="si-select-option" onClick={() => add(q)}>
                <Plus size={14} /> Create &ldquo;{q}&rdquo;
              </button>
            )}
            {q.length === 0 && suggestions.length === 0 && (
              <div className="si-select-empty">No tags</div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

function Thread({ conversation, priority, agent }) {
  if (!conversation) {
    return (
      <div className="si-col si-thread" style={{ alignItems: 'center', justifyContent: 'center' }}>
        <span style={{ color: 'var(--text-muted)' }}>Select a conversation</span>
      </div>
    );
  }
  return (
    <div className="si-col si-thread">
      <div className="si-thread-head">
        <img className="si-avatar" src={conversation.contact.avatar} alt="" />
        <div className="si-thread-headinfo">
          <span className="si-thread-title">
            {conversation.contact.name}
            {conversation.ticketId && (
              <span className="si-thread-ticket">#{conversation.ticketId}</span>
            )}
          </span>
          <span className="si-thread-assignee">
            {agent ? `Agent: ${agent.label.split(' ')[0]}` : 'Unassigned'}
          </span>
        </div>
        <PriorityBadge prio={priority} />
        <div className="si-thread-actions">
          <StatusDropdown
            key={conversation.id}
            initial={
              conversation.status === 'resolved'
                ? { label: 'Resolved', key: 'resolved' }
                : { label: 'Open', key: 'open' }
            }
          />
          <MoreMenu />
        </div>
      </div>

      <div className="si-thread-scroll">
        {conversation.timeline.map((item, idx) => {
          if (item.type === 'divider') {
            return (
              <div key={idx} className="si-divider">
                — {item.label} —
              </div>
            );
          }
          if (item.type === 'event') {
            return (
              <div key={idx} className="si-event">
                {item.label} · {item.at}
              </div>
            );
          }
          const isAgent = item.from === 'agent';
          return (
            <div key={idx} className={`si-msg ${isAgent ? 'si-msg-agent' : ''}`}>
              <span className="si-msg-author">{item.author}</span>
              <div className="si-bubble">{item.body}</div>
              <span className="si-msg-time">{item.at}</span>
            </div>
          );
        })}
      </div>

      <Composer />
    </div>
  );
}

function Accordion({ title, defaultOpen, children }) {
  const [open, setOpen] = useState(!!defaultOpen);
  return (
    <div className="si-accordion">
      <div
        className="si-accordion-head"
        role="button"
        tabIndex={0}
        onClick={() => setOpen((o) => !o)}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            setOpen((o) => !o);
          }
        }}
      >
        {title}
        {open ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
      </div>
      {open && <div className="si-accordion-body">{children}</div>}
    </div>
  );
}

function NotesSection({ initialNotes }) {
  const [notes, setNotes] = useState(initialNotes);
  const [editing, setEditing] = useState(false);
  const [text, setText] = useState('');

  const cancel = () => {
    setText('');
    setEditing(false);
  };
  const save = () => {
    const body = text.trim();
    if (!body) return;
    setNotes((ns) => [{ author: AGENT.name, at: 'just now', body }, ...ns]);
    cancel();
  };

  return (
    <>
      {editing ? (
        <div className="si-note-editor">
          <textarea
            className="si-note-textarea"
            // eslint-disable-next-line jsx-a11y/no-autofocus
            autoFocus
            placeholder="Shift + Enter to add a new line. Ctrl + Enter to send."
            value={text}
            onChange={(e) => setText(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && (e.ctrlKey || e.metaKey)) {
                e.preventDefault();
                save();
              }
            }}
          />
          <div className="si-note-editor-foot">
            <button type="button" className="si-note-btn" onClick={cancel}>
              Cancel
            </button>
            <button type="button" className="si-note-btn si-note-btn-primary" onClick={save}>
              Save note
            </button>
          </div>
        </div>
      ) : (
        <button type="button" className="si-note-add" onClick={() => setEditing(true)}>
          <Plus size={14} /> New note
        </button>
      )}
      {notes.length === 0 && !editing && (
        <span style={{ color: 'var(--text-muted)' }}>No notes yet.</span>
      )}
      {notes.map((n, i) => (
        <div key={i} className="si-note">
          <div className="si-note-meta">
            {n.author} · {n.at}
          </div>
          {n.body}
        </div>
      ))}
    </>
  );
}

function DetailsPanel({ conversation, state, onUpdate, onCollapse }) {
  if (!conversation) return <div className="si-col si-details" />;
  const { contact, attributes, notes, previous = [] } = conversation;
  const agent = AGENTS.find((a) => a.key === state.agentKey) || null;
  const team = TEAM_OPTIONS.find((t) => t.key === state.teamKey) || null;
  const priority =
    state.priority && state.priority !== 'none'
      ? PRIORITY_OPTIONS.find((p) => p.key === state.priority)
      : null;
  return (
    <div className="si-col si-details">
      <div className="si-details-tabs">
        <span className="si-active">Details</span>
        <button
          type="button"
          className="si-collapse-btn si-details-collapse"
          aria-label="Collapse details"
          onClick={onCollapse}
        >
          <PanelLeftClose size={18} className="si-rot180" />
        </button>
      </div>

      <div className="si-contact-head">
        <img className="si-avatar-lg" src={contact.avatar} alt={contact.name} />
        <span className="si-contact-name">{contact.name}</span>
      </div>

      <div className="si-contact-rows">
        <div className="si-contact-row">
          <Mail size={15} /> {contact.email}
        </div>
        <div className="si-contact-row">
          <Phone size={15} /> {contact.availability}
        </div>
        {contact.integrations.map((it) => (
          <div key={it} className="si-contact-row">
            <ExternalLink size={15} /> <span className="si-link">{it}</span>
          </div>
        ))}
      </div>

      <Accordion title="Actions" defaultOpen>
        <div className="si-actions">
          <SearchSelect
            value={agent}
            options={AGENTS}
            placeholder="Select agent"
            onChange={(a) => onUpdate({ agentKey: a.key })}
            renderItem={renderAgent}
          />
          <SearchSelect
            value={team}
            options={TEAM_OPTIONS}
            placeholder="Select team"
            onChange={(t) => onUpdate({ teamKey: t.key })}
            renderItem={renderTeam}
          />
          <SearchSelect
            value={priority}
            options={PRIORITY_OPTIONS}
            placeholder="Select priority"
            onChange={(p) => onUpdate({ priority: p.key })}
            renderItem={renderPriority}
          />
          <TagsInput tags={state.tags} onChange={(tags) => onUpdate({ tags })} />
        </div>
      </Accordion>
      <Accordion title="Contact attributes" defaultOpen>
        {attributes.map((a) => (
          <div key={a.label} className="si-attr">
            <span className="si-attr-label">{a.label}</span>
            <span>{a.value}</span>
          </div>
        ))}
      </Accordion>
      <Accordion title="Notes" defaultOpen>
        <NotesSection key={conversation.id} initialNotes={notes} />
      </Accordion>
      <Accordion title="Previous conversations" defaultOpen>
        {previous.length === 0 && (
          <span style={{ color: 'var(--text-muted)' }}>No previous conversations.</span>
        )}
        {previous.map((p, i) => (
          <div key={i} className="si-prev" role="button" tabIndex={0}>
            <span className="si-prev-title">{p.title}</span>
            <span className="si-prev-time">
              {p.created} · {p.updated}
            </span>
          </div>
        ))}
      </Accordion>
    </div>
  );
}

function PickSelect({ value, onChange, placeholder, options, disabled }) {
  return (
    <div className={`si-field-select ${disabled ? 'si-field-select-disabled' : ''}`}>
      <select value={value} disabled={disabled} onChange={(e) => onChange(e.target.value)}>
        <option value="">{placeholder}</option>
        {options.map((o) => (
          <option key={o} value={o}>
            {o}
          </option>
        ))}
      </select>
      <ChevronsUpDown size={15} className="si-field-select-caret" />
    </div>
  );
}

// Custom field picker for filter conditions: each option row shows the field
// name with a short description underneath.
function FieldPickDropdown({ value, onChange }) {
  const { open, setOpen, ref } = useDropdown();
  return (
    <div className="si-field-select si-fieldpick" ref={ref}>
      <button type="button" className="si-fieldpick-trigger" onClick={() => setOpen((o) => !o)}>
        <span className={value ? '' : 'si-select-ph'}>{value || 'Select field'}</span>
        <ChevronsUpDown size={15} className="si-select-caret" />
      </button>
      {open && (
        <div className="si-select-menu si-fieldpick-menu">
          <div className="si-select-options">
            {FILTER_FIELD_DEFS.map((f) => (
              <button
                key={f.name}
                type="button"
                className={`si-select-option si-fieldpick-option ${
                  value === f.name ? 'si-active' : ''
                }`}
                onClick={() => {
                  onChange(f.name);
                  setOpen(false);
                }}
              >
                <span className="si-fieldpick-name">{f.name}</span>
                <span className="si-fieldpick-desc">{f.desc}</span>
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

// Value control for a filter condition — dropdown, datetime, or free string
// depending on the field. `between` on date fields gets a from/to pair.
function ConditionValue({ field, operator, value, value2, onChange }) {
  const cfg = valueConfigForField(field);
  if (cfg.type === 'options') {
    return (
      <PickSelect
        value={value}
        placeholder="Select value"
        options={cfg.options}
        onChange={(v) => onChange({ value: v })}
      />
    );
  }
  if (cfg.type === 'datetime') {
    return (
      <>
        <input
          type="datetime-local"
          className="si-cond-input"
          value={value}
          onChange={(e) => onChange({ value: e.target.value })}
        />
        {operator === 'between' && (
          <>
            <span className="si-cond-sep">and</span>
            <input
              type="datetime-local"
              className="si-cond-input"
              value={value2}
              onChange={(e) => onChange({ value2: e.target.value })}
            />
          </>
        )}
      </>
    );
  }
  return (
    <input
      type="text"
      className="si-cond-input"
      placeholder="Value"
      value={value}
      onChange={(e) => onChange({ value: e.target.value })}
    />
  );
}

function CreateViewModal({ onClose, onSave }) {
  const [name, setName] = useState('');
  const [groups, setGroups] = useState([newGroup()]);

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onClose]);

  const patchCondition = (gid, cid, patch) =>
    setGroups((gs) =>
      gs.map((g) =>
        g.id === gid
          ? {
              ...g,
              conditions: g.conditions.map((c) => (c.id === cid ? { ...c, ...patch } : c))
            }
          : g
      )
    );

  const addCondition = (gid) =>
    setGroups((gs) =>
      gs.map((g) => (g.id === gid ? { ...g, conditions: [...g.conditions, newCondition()] } : g))
    );

  const removeCondition = (gid, cid) =>
    setGroups((gs) =>
      gs
        .map((g) =>
          g.id === gid ? { ...g, conditions: g.conditions.filter((c) => c.id !== cid) } : g
        )
        .filter((g) => g.conditions.length > 0)
    );

  const addGroup = () => setGroups((gs) => [...gs, newGroup()]);

  return (
    <div className="si-modal-overlay" role="presentation" onClick={onClose}>
      <div
        className="si-modal"
        role="dialog"
        aria-modal="true"
        aria-label="Create View"
        onClick={(e) => e.stopPropagation()}
      >
        <button type="button" className="si-modal-close" aria-label="Close" onClick={onClose}>
          <X size={20} />
        </button>

        <h2 className="si-modal-title">Create View</h2>
        <p className="si-modal-sub">
          Create and save custom filter views for quick access to your conversations.
        </p>

        {/* eslint-disable-next-line jsx-a11y/label-has-associated-control */}
        <label className="si-form-label" htmlFor="si-view-name">
          Name
        </label>
        <input
          id="si-view-name"
          className="si-input"
          value={name}
          onChange={(e) => setName(e.target.value)}
        />
        <p className="si-form-help">Enter an unique name for your view.</p>

        <div className="si-form-label">Filters</div>
        {groups.map((g) => (
          <div key={g.id} className="si-filter-group">
            {g.conditions.map((c) => (
              <div key={c.id} className="si-filter-cond">
                <FieldPickDropdown
                  value={c.field}
                  onChange={(field) =>
                    patchCondition(g.id, c.id, { field, operator: '', value: '', value2: '' })
                  }
                />
                <PickSelect
                  value={c.operator}
                  placeholder="Select operator"
                  options={operatorsForField(c.field)}
                  disabled={!c.field}
                  onChange={(operator) =>
                    patchCondition(g.id, c.id, { operator, value: '', value2: '' })
                  }
                />
                {operatorNeedsValue(c.operator) && (
                  <ConditionValue
                    field={c.field}
                    operator={c.operator}
                    value={c.value}
                    value2={c.value2}
                    onChange={(patch) => patchCondition(g.id, c.id, patch)}
                  />
                )}
                <button
                  type="button"
                  className="si-cond-remove"
                  aria-label="Remove condition"
                  onClick={() => removeCondition(g.id, c.id)}
                >
                  <X size={16} />
                </button>
              </div>
            ))}
            <button type="button" className="si-ghost-btn" onClick={() => addCondition(g.id)}>
              <Plus size={16} /> Add condition
            </button>
          </div>
        ))}

        <button type="button" className="si-ghost-btn si-add-group" onClick={addGroup}>
          <Plus size={16} /> Add group
        </button>
        <p className="si-form-help">Set one or more filters to customize view.</p>

        <div className="si-modal-foot">
          <button type="button" className="si-modal-save" onClick={() => onSave({ name, groups })}>
            Save
          </button>
        </div>
      </div>
    </div>
  );
}

const statusText = (s) => (s === 'resolved' ? 'Resolved' : 'Open');
const lastAt = (c) => {
  const stamped = c.timeline.filter((t) => t.at);
  return stamped.length ? stamped[stamped.length - 1].at : '';
};
const matchesConversation = (c, q) =>
  (c.ticketId || '').toLowerCase().includes(q) ||
  c.contact.name.toLowerCase().includes(q) ||
  c.contact.email.toLowerCase().includes(q) ||
  (c.subject || '').toLowerCase().includes(q) ||
  c.timeline.some((t) => t.type === 'message' && t.body.toLowerCase().includes(q));

// Full-area search overlay: search by ticket #, name, email, or message content.
function SearchOverlay({ navCollapsed, onToggleNav, onOpen }) {
  const [raw, setRaw] = useState('');
  const [q, setQ] = useState('');
  const [tab, setTab] = useState('conversations');

  // Debounce the raw input so results only recompute after typing settles.
  useEffect(() => {
    const t = setTimeout(() => setQ(raw.trim().toLowerCase()), 250);
    return () => clearTimeout(t);
  }, [raw]);

  const convMatches = q ? CONVERSATIONS.filter((c) => matchesConversation(c, q)) : [];
  const msgMatches = q
    ? CONVERSATIONS.flatMap((c) =>
        c.timeline
          .filter((t) => t.type === 'message' && t.body.toLowerCase().includes(q))
          .map((m) => ({ c, m }))
      )
    : [];

  return (
    <div className="si-col si-search">
      <div className="si-search-head">
        <button
          type="button"
          className="si-collapse-btn"
          aria-label={navCollapsed ? 'Expand inbox bar' : 'Collapse inbox bar'}
          onClick={onToggleNav}
        >
          {navCollapsed ? <PanelLeftOpen size={18} /> : <PanelLeftClose size={18} />}
        </button>
        <Search size={16} className="si-search-ico" />
        <input
          // eslint-disable-next-line jsx-a11y/no-autofocus
          autoFocus
          className="si-search-input"
          placeholder="Search"
          value={raw}
          onChange={(e) => setRaw(e.target.value)}
        />
      </div>

      {!q ? (
        <div className="si-search-empty">
          <h2>Search conversations</h2>
          <p>Search by reference number, contact email address or messages in conversations.</p>
        </div>
      ) : (
        <div className="si-search-body">
          <div className="si-search-tabs" role="tablist">
            <button
              type="button"
              role="tab"
              aria-selected={tab === 'conversations'}
              className={`si-search-tab ${tab === 'conversations' ? 'si-active' : ''}`}
              onClick={() => setTab('conversations')}
            >
              Conversations ({convMatches.length})
            </button>
            <button
              type="button"
              role="tab"
              aria-selected={tab === 'messages'}
              className={`si-search-tab ${tab === 'messages' ? 'si-active' : ''}`}
              onClick={() => setTab('messages')}
            >
              Messages ({msgMatches.length})
            </button>
          </div>

          {tab === 'conversations' ? (
            <div className="si-search-results">
              {convMatches.length === 0 && (
                <div className="si-search-none">No conversations found.</div>
              )}
              {convMatches.map((c) => (
                <button
                  key={c.id}
                  type="button"
                  className="si-sr-card"
                  onClick={() => onOpen(c.id)}
                >
                  <div className="si-sr-main">
                    <div className="si-sr-top">
                      <span className="si-sr-id">#{c.ticketId}</span>
                      <span className="si-sr-status">{statusText(c.status)}</span>
                    </div>
                    <div className="si-sr-time">
                      <Clock size={14} /> {lastAt(c)}
                    </div>
                  </div>
                  <span className="si-sr-go">
                    <ChevronRight size={16} />
                  </span>
                </button>
              ))}
            </div>
          ) : (
            <div className="si-search-results">
              {msgMatches.length === 0 && <div className="si-search-none">No messages found.</div>}
              {msgMatches.map(({ c, m }, i) => (
                <button
                  key={`${c.id}-${i}`}
                  type="button"
                  className="si-sr-card"
                  onClick={() => onOpen(c.id)}
                >
                  <div className="si-sr-main">
                    <div className="si-sr-top">
                      <span className="si-sr-id">#{c.ticketId}</span>
                      <span className="si-sr-author">{m.author}</span>
                    </div>
                    <div className="si-sr-snippet">{m.body}</div>
                  </div>
                  <span className="si-sr-go">
                    <ChevronRight size={16} />
                  </span>
                </button>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export default function SupportInbox() {
  const [activeFolder, setActiveFolder] = useState('all');
  const [selectedId, setSelectedId] = useState(CONVERSATIONS[0].id);
  const [navCollapsed, setNavCollapsed] = useState(false);
  const [detailsCollapsed, setDetailsCollapsed] = useState(false);
  const [showCreateView, setShowCreateView] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);

  // Per-conversation editable fields (agent, team, priority, tags). Lifted here
  // so a change in the Actions panel reflects live in the list and thread header.
  const [convState, setConvState] = useState(() =>
    Object.fromEntries(
      CONVERSATIONS.map((c) => [
        c.id,
        {
          agentKey: c.assignee === 'Pankaj Soni' ? 'pankaj' : null,
          teamKey: c.inbox,
          priority: c.priority,
          tags: c.tags
        }
      ])
    )
  );
  const updateConv = (id, patch) => setConvState((s) => ({ ...s, [id]: { ...s[id], ...patch } }));

  const conversations = useMemo(() => {
    if (activeFolder === 'all') return CONVERSATIONS;
    if (activeFolder === 'my_inbox') {
      return CONVERSATIONS.filter((c) => c.assignee === AGENT.name);
    }
    if (activeFolder === 'unassigned') {
      return CONVERSATIONS.filter((c) => !c.assignee);
    }
    // team inbox keys match conversation.inbox
    const byInbox = CONVERSATIONS.filter((c) => c.inbox === activeFolder);
    return byInbox.length ? byInbox : CONVERSATIONS;
  }, [activeFolder]);

  const selected = conversations.find((c) => c.id === selectedId) || conversations[0] || null;

  const activeLabel = useMemo(() => {
    const folder = FOLDERS.find((x) => x.key === activeFolder);
    if (folder) return folder.label;
    const team = TEAM_INBOXES.find((x) => x.key === activeFolder);
    if (team) return `Team: ${team.label}`;
    const view = VIEWS.find((x) => x.key === activeFolder);
    if (view) return `View: ${view.label}`;
    return 'All';
  }, [activeFolder]);

  return (
    <>
      <Helmet>
        <title>Support Inbox · Hridaya</title>
      </Helmet>
      <div
        className={`si-shell ${navCollapsed ? 'si-nav-collapsed' : ''} ${
          detailsCollapsed ? 'si-details-collapsed' : ''
        } ${searchOpen ? 'si-search-open' : ''}`}
      >
        {!navCollapsed && (
          <NavRail
            activeFolder={activeFolder}
            onSelectFolder={(k) => {
              setSearchOpen(false);
              setActiveFolder(k);
            }}
            onCreateView={() => setShowCreateView(true)}
            onSearch={() => setSearchOpen(true)}
          />
        )}
        {searchOpen ? (
          <SearchOverlay
            navCollapsed={navCollapsed}
            onToggleNav={() => setNavCollapsed((v) => !v)}
            onOpen={(id) => {
              setActiveFolder('all');
              setSelectedId(id);
              setSearchOpen(false);
            }}
          />
        ) : (
          <>
            <ConversationList
              conversations={conversations}
              convState={convState}
              selectedId={selected ? selected.id : null}
              onSelect={setSelectedId}
              navCollapsed={navCollapsed}
              onToggleNav={() => setNavCollapsed((v) => !v)}
              title={activeLabel}
            />
            <Thread
              conversation={selected}
              priority={selected ? convState[selected.id].priority : null}
              agent={
                selected
                  ? AGENTS.find((a) => a.key === convState[selected.id].agentKey) || null
                  : null
              }
            />
            {!detailsCollapsed && (
              <DetailsPanel
                conversation={selected}
                state={selected ? convState[selected.id] : null}
                onUpdate={(patch) => selected && updateConv(selected.id, patch)}
                onCollapse={() => setDetailsCollapsed(true)}
              />
            )}
            {detailsCollapsed && (
              <button
                type="button"
                className="si-details-expand"
                aria-label="Expand details"
                onClick={() => setDetailsCollapsed(false)}
              >
                <PanelLeftOpen size={18} className="si-rot180" />
              </button>
            )}
          </>
        )}
      </div>
      {showCreateView && (
        <CreateViewModal
          onClose={() => setShowCreateView(false)}
          onSave={() => setShowCreateView(false)}
        />
      )}
    </>
  );
}
